import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Activity,
  Archive,
  BookOpen,
  Briefcase,
  Building2,
  CalendarCheck,
  CalendarDays,
  CircleCheck,
  CircleDashed,
  CircleDot,
  ClipboardList,
  Clock,
  FileText,
  GraduationCap,
  History,
  Hourglass,
  LifeBuoy,
  Loader,
  LogIn,
  Megaphone,
  Package,
  Printer,
  Receipt,
  ShieldCheck,
  Smartphone,
  Ticket,
  Timer,
  UserCheck,
  Users,
  Video,
  Wallet,
} from 'lucide-react';

import { Badge } from '@/components/ui/Badge';
import { BarChart } from '@/components/ui/BarChart';
import { Button } from '@/components/ui/Button';
import { DonutChart } from '@/components/ui/DonutChart';
import { EmptyState } from '@/components/ui/EmptyState';
import { FigureTile } from '@/components/ui/FigureTile';
import { LineChart } from '@/components/ui/LineChart';
import { ProgressBar } from '@/components/ui/ProgressBar';
import { QueryError } from '@/components/ui/QueryError';
import { SecureImage } from '@/components/ui/SecureImage';
import { Skeleton, SkeletonLines } from '@/components/ui/Skeleton';
import { Tabs } from '@/components/ui/Tabs';
import {
  INSTITUTION_TYPE_LABEL,
  PAYMENT_STATUS_LABEL,
  PAYMENT_STATUS_TONE,
  SCHOOL_STATUS_LABEL,
  SCHOOL_STATUS_TONE,
  SUBSCRIPTION_PAYMENT_METHOD_LABEL,
} from '@/constants/institute';
import { TICKET_STATUS_LABEL } from '@/constants/support';
import { useApiQuery } from '@/hooks/useApiQuery';
import { formatCurrency, formatDate, formatDateTime, formatDuration, formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { InstituteReport, ReportPeopleGroup, ReportPeriod } from '@/types/instituteReport';
import { DeckPageHeader } from '../../components/DeckPageHeader';
import { institutesApi } from '../../institutes/api/institutes.api';
import { isSubscriptionOverdue } from '../../institutes/utils';
import { reportsApi } from '../api/reports.api';
import { ActivityByDateTable } from '../components/ActivityByDateTable';
import { ReportCountTile } from '../components/ReportCountTile';
import { ReportKpi } from '../components/ReportKpi';
import { ReportSection } from '../components/ReportSection';
import { REPORT_PERIOD_OPTIONS, type ReportPeriodOption } from '../constants';
import { dayLabels, eventLabel, monthLabels, percentChange } from '../utils';

const GROUP_LABEL: Record<ReportPeopleGroup, string> = {
  admins: 'Admins',
  teachers: 'Teachers',
  students: 'Students',
  staff: 'Other staff',
  parents: 'Parents',
};

/** Whole percent, "—" when there is nothing to divide by. */
function share(part: number, whole: number): string {
  return whole === 0 ? '—' : `${Math.round((part / whole) * 100)}%`;
}

function minutesLabel(minutes: number): string {
  return formatDuration(Math.round(minutes)) ?? '0 mins';
}

/** A ticket's time to resolve — in days once it runs past one, where hours stop meaning much. */
function resolutionLabel(minutes: number): string {
  if (minutes < 24 * 60) return minutesLabel(minutes);
  const days = Math.round((minutes / (24 * 60)) * 10) / 10;
  return `${formatNumber(days)} days`;
}

/**
 * One customer at a glance — how much they use the product, who uses it, what
 * they have, what they pay, and what we have done for them lately.
 *
 * Read top to bottom as a story: who the school has, how much they use the
 * product, then the active people and sign-ins day by day and month by month,
 * the days themselves as a table, the work the school runs through the app,
 * the help it has asked us for, and finally the commercial side. Figures are
 * for the period chosen at the top unless they say otherwise — the monthly
 * ones always cover whole months, so they do not move with it.
 *
 * Printable: the browser's Save as PDF gives a copy to send to the school's
 * management, which is what the Print button is for.
 */
export function InstituteReportPage() {
  const { uuid = '' } = useParams();
  const [period, setPeriod] = useState<ReportPeriodOption>('30');
  const days = Number(period) as ReportPeriod;

  const { data: report, isLoading, error, refetch } = useApiQuery(
    () => reportsApi.institute(uuid, days),
    [uuid, days],
  );

  if (error) {
    return <QueryError title="Could not load this report" error={error} onRetry={refetch} />;
  }

  return (
    <div className="flex flex-col gap-6">
      <DeckPageHeader
        eyebrow="Institute report"
        title={report?.institute.displayName ?? 'Loading report…'}
        description={
          report
            ? `${INSTITUTION_TYPE_LABEL[report.institute.institutionType]} · ${report.institute.city}, ${report.institute.state} · ${formatDate(report.period.from)} – ${formatDate(report.period.to)}`
            : 'Gathering this customer’s figures.'
        }
        backTo={{ to: `/platform/institutes/${uuid}`, label: 'Back to the institute' }}
        mark={
          report?.institute.hasLogo ? (
            <SecureImage
              path={institutesApi.logoPath(uuid)}
              alt={`${report.institute.displayName} logo`}
              className="h-full w-full p-1"
              fallback={<Building2 className="h-5 w-5 text-accent" />}
            />
          ) : (
            <Building2 className="h-5 w-5 text-accent" aria-hidden />
          )
        }
        actions={
          <>
            <Tabs
              options={REPORT_PERIOD_OPTIONS}
              value={period}
              onChange={setPeriod}
              label="Report period"
              variant="segmented"
            />
            <Button
              variant="secondary"
              leftIcon={<Printer className="h-4 w-4" />}
              onClick={() => window.print()}
              disabled={!report}
            >
              Print / PDF
            </Button>
          </>
        }
      >
        {report && (
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={SCHOOL_STATUS_TONE[report.institute.status]} withDot>
              {SCHOOL_STATUS_LABEL[report.institute.status]}
            </Badge>
            <span className="rounded-pill border border-line bg-surface px-3 py-1 font-mono text-2xs uppercase text-ink-2">
              {report.institute.code}
            </span>
            <span className="rounded-pill border border-line bg-surface px-3 py-1 text-2xs text-ink-2">
              {/* Only a customer once they went live: an institute still in
                  setup was added by us but has never used the product. */}
              {report.institute.activatedAt
                ? `Customer since ${formatDate(report.institute.activatedAt)}`
                : `Not live yet · added ${formatDate(report.institute.createdAt)}`}
            </span>
            <span className="rounded-pill border border-line bg-surface px-3 py-1 text-2xs text-ink-2">
              Last sign-in {report.headline.lastLoginAt ? formatDateTime(report.headline.lastLoginAt) : 'never'}
            </span>
          </div>
        )}
      </DeckPageHeader>

      {isLoading || !report ? <ReportSkeleton /> : <ReportBody report={report} />}
    </div>
  );
}

function ReportBody({ report }: { report: InstituteReport }) {
  const { headline, headcount, usage, logins, people, timeInApp, features, activity, subscription, tickets } = report;
  const loginChange = percentChange(headline.logins, headline.previousLogins);
  const timeChange = percentChange(headline.avgMinutesPerDay, headline.previousAvgMinutesPerDay);

  const loginPoints = logins.byDay.map((day) => ({
    ...dayLabels(day.day),
    values: { web: day.web, app: day.app },
  }));
  const activePoints = usage.byDay.map((day) => ({
    ...dayLabels(day.day),
    values: { activeUsers: day.activeUsers },
  }));
  const timePoints = timeInApp.byDay.map((day) => ({
    ...dayLabels(day.day),
    values: { admins: day.admins, teachers: day.teachers, students: day.students, staff: day.staff },
  }));
  const monthlyActivePoints = usage.byMonth.map((month) => ({
    ...monthLabels(month.month),
    values: { activeUsers: month.activeUsers },
  }));
  const monthlyTimePoints = usage.byMonth.map((month) => ({
    ...monthLabels(month.month),
    values: { hours: month.hours },
  }));
  const hasTime = timeInApp.byDay.some(
    (day) => day.admins !== null || day.teachers !== null || day.students !== null || day.staff !== null,
  );
  const hasActive = usage.byDay.some((day) => day.activeUsers > 0);
  const hasMonthlyActive = usage.byMonth.some((month) => month.activeUsers > 0);
  const hasMonthlyTime = usage.byMonth.some((month) => month.hours > 0);
  const periodMinutes = usage.byDay.reduce((total, day) => total + day.minutes, 0);

  const groups = Object.keys(people.byGroup) as ReportPeopleGroup[];
  const overdue = isSubscriptionOverdue(subscription.dueDate, subscription.paymentStatus);

  const headcountTiles = [
    { label: 'Total students', value: headcount.students, hint: 'Enrolled and active', icon: GraduationCap, tone: 'accent' },
    { label: 'Total teachers', value: headcount.teachers, hint: 'Teacher accounts', icon: Users, tone: 'info' },
    { label: 'Total admins', value: headcount.admins, hint: 'Administrators and principals', icon: ShieldCheck, tone: 'pick' },
    { label: 'Total staff', value: headcount.staff, hint: 'Office, library, transport', icon: Briefcase, tone: 'warn' },
    {
      label: 'App users',
      value: headline.appUsers,
      hint: 'People signed in on the mobile app',
      icon: Smartphone,
      tone: 'ok',
    },
  ] as const;

  const activityTiles = [
    { label: 'Attendance days', value: activity.attendanceDays, icon: CalendarCheck },
    { label: 'Homework set', value: activity.homework, icon: BookOpen },
    { label: 'Activities', value: activity.activities, icon: Activity },
    { label: 'Exams created', value: activity.exams, icon: ClipboardList },
    { label: 'Notices posted', value: activity.notices, icon: Megaphone },
    { label: 'Recorded classes', value: activity.recordings, icon: Video },
    { label: 'Daily reports', value: activity.dailyReports, icon: FileText },
    { label: 'Fee receipts', value: activity.feeReceipts, icon: Receipt },
  ];

  const ticketTiles = [
    { label: TICKET_STATUS_LABEL.OPEN, value: tickets.byStatus.OPEN, icon: CircleDot },
    { label: TICKET_STATUS_LABEL.IN_PROGRESS, value: tickets.byStatus.IN_PROGRESS, icon: Loader },
    { label: TICKET_STATUS_LABEL.WAITING_ON_SCHOOL, value: tickets.byStatus.WAITING_ON_SCHOOL, icon: Hourglass },
    { label: TICKET_STATUS_LABEL.RESOLVED, value: tickets.byStatus.RESOLVED, icon: CircleCheck },
    { label: TICKET_STATUS_LABEL.CLOSED, value: tickets.byStatus.CLOSED, icon: Archive },
    { label: `Raised in ${report.period.days} days`, value: tickets.raisedInPeriod, icon: Ticket },
    { label: `Resolved in ${report.period.days} days`, value: tickets.resolvedInPeriod, icon: CircleCheck },
    {
      label: 'Average time to resolve',
      value: tickets.avgResolutionMinutes === null ? '—' : resolutionLabel(tickets.avgResolutionMinutes),
      icon: Timer,
    },
  ];

  return (
    <>
      {/* ---------------- who the school has ---------------- */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5">
        {headcountTiles.map(({ label, value, hint, icon: Icon, tone }) => (
          <FigureTile
            key={label}
            label={label}
            value={formatNumber(value)}
            hint={hint}
            icon={<Icon className="h-5 w-5" />}
            tone={tone}
            layout="stacked"
          />
        ))}
      </div>

      {/* ---------------- how much they use it ---------------- */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <ReportKpi
          label="Total sign-ins"
          value={formatNumber(headline.logins)}
          icon={<LogIn className="h-[18px] w-[18px]" />}
          change={loginChange}
          isNew={headline.logins > 0 && headline.previousLogins === 0}
          hint={`By ${formatNumber(usage.uniqueSignIns)} ${usage.uniqueSignIns === 1 ? 'person' : 'people'} · ${formatNumber(headline.previousLogins)} in the ${report.period.days} days before`}
        />
        <ReportKpi
          label="Active users"
          value={formatNumber(usage.activeUsers)}
          icon={<UserCheck className="h-[18px] w-[18px]" />}
          hint={`Signed in or used the app in ${report.period.days} days`}
        />
        <ReportKpi
          label="Daily active users"
          value={formatNumber(usage.dailyActiveUsers.average)}
          icon={<CalendarDays className="h-[18px] w-[18px]" />}
          hint={`Average per day · ${formatNumber(usage.dailyActiveUsers.today)} today`}
        />
        <ReportKpi
          label="Monthly active users"
          value={formatNumber(usage.monthlyActiveUsers)}
          icon={<Users className="h-[18px] w-[18px]" />}
          hint={`Active in the last 30 days · ${share(usage.monthlyActiveUsers, headline.accounts)} of accounts`}
        />
        <ReportKpi
          label="Daily app usage"
          value={minutesLabel(headline.avgMinutesPerDay)}
          icon={<Clock className="h-[18px] w-[18px]" />}
          change={timeChange}
          isNew={headline.avgMinutesPerDay > 0 && headline.previousAvgMinutesPerDay === 0}
          hint="Average per person, on a day they used it"
        />
        <ReportKpi
          label="Monthly app usage"
          value={minutesLabel(usage.monthlyMinutesPerPerson)}
          icon={<Timer className="h-[18px] w-[18px]" />}
          hint={`Per person in the last 30 days · ${minutesLabel(usage.monthlyMinutes)} in all`}
        />
        <ReportKpi
          label="Time spent this period"
          value={minutesLabel(periodMinutes)}
          icon={<Clock className="h-[18px] w-[18px]" />}
          hint={`Everyone together, last ${report.period.days} days`}
        />
        <ReportKpi
          label="Ever logged in"
          value={share(headline.everLoggedIn, headline.accounts)}
          icon={<Smartphone className="h-[18px] w-[18px]" />}
          hint={`${formatNumber(headline.everLoggedIn)} of ${formatNumber(headline.accounts)} accounts`}
        />
      </div>

      {/* ---------------- active people, day by day ---------------- */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <ReportSection
          className="lg:col-span-2"
          title="Daily active users"
          description="How many different people signed in or used the app, each day."
          icon={<UserCheck className="h-4 w-4" />}
        >
          {hasActive ? (
            <LineChart
              title="Active users per day"
              series={[{ key: 'activeUsers', label: 'Active users', tone: 1 }]}
              points={activePoints}
              height={220}
              formatValue={(value) => formatNumber(Math.round(value))}
            />
          ) : (
            <EmptyState title="Nobody was active in this period" description="Try a longer period above." />
          )}
        </ReportSection>

        <ReportSection title="Web or app" description="Where the sign-ins came from." icon={<Smartphone className="h-4 w-4" />}>
          {logins.total === 0 ? (
            <p className="text-sm text-ink-3">Nothing to split yet.</p>
          ) : (
            <DonutChart
              total={logins.total}
              caption="Logins"
              slices={[
                { key: 'web', label: 'Web', value: logins.web, stroke: 'stroke-series-1', dot: 'bg-series-1' },
                { key: 'app', label: 'App', value: logins.app, stroke: 'stroke-series-2', dot: 'bg-series-2' },
              ]}
              formatValue={formatNumber}
            />
          )}
        </ReportSection>
      </div>

      {/* ---------------- sign-ins and who ---------------- */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <ReportSection
          className="lg:col-span-2"
          title="Logins per day"
          description="Every sign-in, web and mobile app stacked."
          icon={<LogIn className="h-4 w-4" />}
        >
          {logins.total === 0 ? (
            <EmptyState title="No sign-ins in this period" description="Try a longer period above." />
          ) : (
            <BarChart
              title="Logins per day"
              series={[
                { key: 'web', label: 'Web', tone: 1 },
                { key: 'app', label: 'App', tone: 2 },
              ]}
              points={loginPoints}
              height={220}
            />
          )}
        </ReportSection>

        <ReportSection
          title="Who has signed in"
          description="Accounts by role, and how many have ever used them."
          icon={<Users className="h-4 w-4" />}
        >
          <ul className="flex flex-col gap-4">
            {groups.map((group) => {
              const figures = people.byGroup[group];
              const pct = figures.accounts === 0 ? 0 : (figures.everLoggedIn / figures.accounts) * 100;
              return (
                <li key={group} className="flex flex-col gap-1.5">
                  <ProgressBar
                    label={GROUP_LABEL[group]}
                    value={pct}
                    caption={`${formatNumber(figures.everLoggedIn)} of ${formatNumber(figures.accounts)} (${share(figures.everLoggedIn, figures.accounts)})`}
                  />
                  {figures.appUsers > 0 && (
                    <p className="text-2xs text-ink-3">{formatNumber(figures.appUsers)} used the mobile app</p>
                  )}
                </li>
              );
            })}
          </ul>
          <p className="rounded-field bg-surface-2 px-3 py-2 text-xs text-ink-2">
            {formatNumber(people.studentsEnrolled)} students enrolled · {formatNumber(people.byGroup.students.accounts)}{' '}
            have a login account of their own.
          </p>
        </ReportSection>
      </div>

      {/* ---------------- month by month ---------------- */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ReportSection
          title="Monthly active users"
          description={`Different people active in each of the last ${usage.byMonth.length} months.`}
          icon={<Users className="h-4 w-4" />}
        >
          {hasMonthlyActive ? (
            <BarChart
              title="Monthly active users"
              series={[{ key: 'activeUsers', label: 'Active users', tone: 1 }]}
              points={monthlyActivePoints}
              height={200}
            />
          ) : (
            <EmptyState title="Nobody active yet" description="Months fill in as people sign in." />
          )}
        </ReportSection>

        <ReportSection
          title="Monthly time spent"
          description="Hours in the app each month, everyone together."
          icon={<Timer className="h-4 w-4" />}
        >
          {hasMonthlyTime ? (
            <BarChart
              title="Hours in the app per month"
              series={[{ key: 'hours', label: 'Hours', tone: 2 }]}
              points={monthlyTimePoints}
              height={200}
              formatValue={(value) => `${formatNumber(value)} hr`}
            />
          ) : (
            <EmptyState
              icon={<Clock className="h-6 w-6" />}
              title="No time recorded yet"
              description="Time is counted as people use the web app."
            />
          )}
        </ReportSection>
      </div>

      {/* ---------------- time by role, and by date ---------------- */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ReportSection
          title="Daily time in the app"
          description="Average minutes per person on each day, by role."
          icon={<Clock className="h-4 w-4" />}
        >
          {hasTime ? (
            <LineChart
              title="Minutes in the app per person, per day"
              series={[
                { key: 'teachers', label: 'Teachers', tone: 1 },
                { key: 'students', label: 'Students', tone: 2 },
                { key: 'staff', label: 'Other staff', tone: 3 },
                { key: 'admins', label: 'Admins', tone: 4 },
              ]}
              points={timePoints}
              height={220}
              formatValue={(value) => `${Math.round(value)}m`}
            />
          ) : (
            <EmptyState
              icon={<Clock className="h-6 w-6" />}
              title="No time recorded yet"
              description="Time is counted as people use the web app. It fills in from the first day of use."
            />
          )}
        </ReportSection>

        <ReportSection
          title="User activity by date"
          description="Each day of the period, newest first."
          icon={<CalendarDays className="h-4 w-4" />}
        >
          <ActivityByDateTable days={usage.byDay} />
        </ReportSection>
      </div>

      {/* ---------------- work done in the app ---------------- */}
      <ReportSection
        title="Running the school in the app"
        description={`What the school did through the product in the last ${report.period.days} days.`}
        icon={<CalendarCheck className="h-4 w-4" />}
      >
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {activityTiles.map((tile) => (
            <ReportCountTile key={tile.label} {...tile} />
          ))}
        </div>
      </ReportSection>

      {/* ---------------- support ---------------- */}
      <ReportSection
        title="Support tickets"
        description={`${formatNumber(tickets.total)} raised in all. Status counts are as of now; the rest cover the last ${report.period.days} days.`}
        icon={<LifeBuoy className="h-4 w-4" />}
        // Opens the queue on this institute's tickets, not everybody's.
        aside={
          <Link to={`/platform/support?institute=${report.institute.uuid}`} className="shrink-0 text-xs font-[560] text-accent hover:text-ink">
            Open support desk
          </Link>
        }
      >
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {ticketTiles.map((tile) => (
            <ReportCountTile key={tile.label} {...tile} />
          ))}
        </div>
      </ReportSection>

      {/* ---------------- commercial ---------------- */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ReportSection
          title="Features"
          description="What they asked for, what is built, and what they can use."
          icon={<Package className="h-4 w-4" />}
          aside={
            <Link
              to={`/platform/features?institute=${report.institute.uuid}`}
              className="shrink-0 text-xs font-[560] text-accent hover:text-ink"
            >
              Open tracker
            </Link>
          }
        >
          {features.summary.tracked === 0 ? (
            <p className="text-sm text-ink-3">No features on this institute’s tracker yet.</p>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3 text-center">
                {[
                  { label: 'Requested', value: features.summary.tracked },
                  { label: 'Active', value: features.summary.enabled },
                ].map((item) => (
                  <div key={item.label} className="rounded-field border border-line bg-surface-2 py-3">
                    <p className="text-xl font-[750] tabular-nums text-ink">{item.value}</p>
                    <p className="text-2xs uppercase tracking-label text-ink-3">{item.label}</p>
                  </div>
                ))}
              </div>
              <ul className="flex flex-col divide-y divide-line">
                {features.items.map((feature) => (
                  <li key={feature.uuid} className="flex items-center justify-between gap-3 py-2 text-sm">
                    <span className="flex min-w-0 items-center gap-2">
                      {feature.isEnabled ? (
                        <CircleCheck className="h-4 w-4 shrink-0 text-ok" aria-label="Active" />
                      ) : (
                        <CircleDashed className="h-4 w-4 shrink-0 text-ink-3" aria-label="Not active" />
                      )}
                      <span className="truncate text-ink">{feature.name}</span>
                    </span>
                    <span className="shrink-0 tabular-nums text-ink-2">
                      {feature.price === null ? 'Base licence' : formatCurrency(feature.price)}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="flex items-center justify-between border-t border-line pt-3 text-sm">
                <span className="text-ink-2">Total at list price</span>
                <span className="font-[650] tabular-nums text-ink">{formatCurrency(features.summary.totalPrice)}</span>
              </p>
            </>
          )}
        </ReportSection>

        <ReportSection
          title="Subscription"
          description="The licence fee and what has been paid."
          icon={<Wallet className="h-4 w-4" />}
          aside={
            <Link
              to={`/platform/institutes/${report.institute.uuid}/subscription`}
              className="shrink-0 text-xs font-[560] text-accent hover:text-ink"
            >
              Payment history
            </Link>
          }
        >
          <dl className="grid grid-cols-2 gap-3">
            {[
              { label: 'Licence fee', value: formatCurrency(subscription.amount) },
              {
                label: 'Status',
                value: (
                  <Badge tone={PAYMENT_STATUS_TONE[subscription.paymentStatus]} withDot>
                    {PAYMENT_STATUS_LABEL[subscription.paymentStatus]}
                  </Badge>
                ),
              },
              {
                label: 'Due date',
                value: subscription.dueDate ? (
                  <span className={cn(overdue && 'text-crit')}>
                    {overdue && 'Overdue · '}
                    {formatDate(subscription.dueDate)}
                  </span>
                ) : (
                  'Not set'
                ),
              },
              {
                label: 'Paid to date',
                value: `${formatCurrency(subscription.totalPaid)} · ${subscription.paymentCount} payment${subscription.paymentCount === 1 ? '' : 's'}`,
              },
            ].map((item) => (
              <div key={item.label} className="rounded-field border border-line bg-surface-2 p-3">
                <dt className="text-2xs uppercase tracking-label text-ink-3">{item.label}</dt>
                <dd className="mt-1 text-sm font-[650] text-ink">{item.value}</dd>
              </div>
            ))}
          </dl>
          {subscription.lastPaidOn && (
            <p className="text-xs text-ink-3">
              Last payment {formatCurrency(subscription.lastPaidAmount)} on {formatDate(subscription.lastPaidOn)}.
            </p>
          )}
          {subscription.recentPayments.length > 0 && (
            <div className="flex flex-col gap-1">
              <p className="lbl">Recent payments</p>
              <ul className="flex flex-col divide-y divide-line">
                {subscription.recentPayments.map((payment) => (
                  <li key={payment.uuid} className="flex items-center justify-between gap-3 py-2 text-sm">
                    <span className="min-w-0">
                      <span className="block truncate text-ink">
                        {formatDate(payment.paidOn)} · {payment.periodYear}
                      </span>
                      <span className="block text-xs text-ink-3">
                        {SUBSCRIPTION_PAYMENT_METHOD_LABEL[payment.method]}
                      </span>
                    </span>
                    <span className="shrink-0 font-[650] tabular-nums text-ink">{formatCurrency(payment.amount)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </ReportSection>
      </div>

      {/* ---------------- what we did ---------------- */}
      <ReportSection
        title="Recent console activity"
        description="What our team has done for this institute lately."
        icon={<History className="h-4 w-4" />}
      >
        {report.recentEvents.length === 0 ? (
          <p className="text-sm text-ink-3">Nothing recorded yet.</p>
        ) : (
          <ol className="relative flex flex-col gap-4 border-l border-line pl-5">
            {report.recentEvents.map((event) => (
              <li key={`${event.action}-${event.at}`} className="relative">
                <span
                  aria-hidden
                  className="absolute -left-[25px] top-1 h-2.5 w-2.5 rounded-pill border-2 border-surface bg-accent"
                />
                <p className="text-sm font-[560] text-ink">{eventLabel(event.action)}</p>
                <p className="text-xs text-ink-3">
                  {formatDateTime(event.at)}
                  {event.by && ` · ${event.by}`}
                </p>
              </li>
            ))}
          </ol>
        )}
      </ReportSection>
    </>
  );
}

/** The report's own loading shape, so the page does not jump when it lands. */
function ReportSkeleton() {
  return (
    <div className="flex flex-col gap-4" role="status" aria-label="Loading report">
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5">
        {Array.from({ length: 5 }, (_, index) => (
          <div key={index} className="card flex flex-col gap-3 p-4">
            <Skeleton className="h-3 w-2/3" />
            <Skeleton className="h-8 w-1/3" />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }, (_, index) => (
          <div key={index} className="card flex flex-col gap-3 p-5">
            <Skeleton className="h-3 w-1/3" />
            <Skeleton className="h-8 w-1/2" />
            <Skeleton className="h-3 w-2/3" />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="card p-5 lg:col-span-2">
          <Skeleton className="h-56 w-full" />
        </div>
        <div className="card p-5">
          <SkeletonLines count={6} />
        </div>
      </div>
    </div>
  );
}
